import type {
	AgentAvailability,
	AgentEvent,
	AgentProvider,
	DesktopBridge,
} from "@pluck/desktop-bridge";
import {
	AlertTriangle,
	Check,
	Loader2,
	Plus,
	Send,
	Square,
	Wrench,
	X,
} from "lucide-react";
import {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	addUserMessage,
	applyAgentEvent,
	type Chat,
	type ChatItem,
	createChat,
	loadChats,
	saveChats,
	settleRunningTools,
} from "@/lib/agent-chats";
import { track } from "@/lib/analytics";
import { getDesktopBridge } from "@/lib/desktop";
import { env } from "@/lib/env";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";

const PROVIDER_LABELS: Record<AgentProvider, string> = {
	codex: "Codex",
	claude: "Claude",
};
const SUGGESTIONS = [
	"What's on this board?",
	"Add a prompt that combines all the plucks, then run it",
	"Look at the latest generation and suggest improvements",
];

/** Whether a stored token still works (it may have been revoked). */
async function tokenWorks(token: string): Promise<boolean> {
	try {
		const res = await fetch(`${env.VITE_SERVER_URL}/trpc/boards.list`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		return res.status !== 401;
	} catch {
		// Offline: assume it's fine; the agent will report errors.
		return true;
	}
}

let checkedToken: string | null = null;

/** The desktop chat keeps one access token, stored encrypted by the app. */
async function ensureAgentToken(bridge: DesktopBridge): Promise<string> {
	const stored = await bridge.getAgentToken();
	if (stored && (stored === checkedToken || (await tokenWorks(stored)))) {
		checkedToken = stored;
		return stored;
	}
	const created = await trpcClient.tokens.create.mutate({
		name: "Pluck Desktop chat",
	});
	await bridge.setAgentToken(created.token);
	checkedToken = created.token;
	return created.token;
}

function ToolRow({ item }: { item: Extract<ChatItem, { kind: "tool" }> }) {
	let icon = <Loader2 className="size-3 animate-spin" />;
	if (item.status === "done") {
		icon = <Check className="size-3 text-emerald-600" />;
	} else if (item.status === "error") {
		icon = <AlertTriangle className="size-3 text-destructive" />;
	}
	return (
		<details className="group/tool rounded-md border bg-muted/40 px-2 py-1 text-[11px]">
			<summary className="flex cursor-pointer list-none items-center gap-1.5 text-muted-foreground">
				<Wrench className="size-3" />
				<span className="font-medium text-foreground">{item.name}</span>
				<span className="ml-auto">{icon}</span>
			</summary>
			{item.detail && (
				<p className="mt-1 whitespace-pre-wrap break-words text-muted-foreground">
					{item.detail}
				</p>
			)}
		</details>
	);
}

function ChatMessage({ item }: { item: ChatItem }) {
	if (item.kind === "tool") {
		return <ToolRow item={item} />;
	}
	return (
		<div
			className={cn(
				"whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm",
				item.kind === "user" && "ml-8 bg-primary text-primary-foreground",
				item.kind === "assistant" && "mr-4 bg-muted",
				item.kind === "error" &&
					"mr-4 border border-destructive/30 bg-destructive/5 text-destructive"
			)}
		>
			{item.text}
		</div>
	);
}

function WebNotice({ onClose }: { onClose: () => void }) {
	useEffect(() => {
		track("agent_chat.unavailable_viewed", { reason: "web" });
	}, []);
	return (
		<aside className="flex w-80 shrink-0 flex-col border-l bg-card">
			<div className="flex h-12 items-center justify-between border-b px-3">
				<p className="font-medium text-sm">Chat</p>
				<Button
					aria-label="Close chat"
					onClick={onClose}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>
			<p className="p-4 text-muted-foreground text-sm">
				The board chat runs your own Codex or Claude on your computer. Open this
				board in Pluck Desktop to use it.
			</p>
		</aside>
	);
}

/** A chat turn in progress, for analytics. */
interface TurnStats {
	provider: AgentProvider;
	startedAt: number;
	toolErrors: number;
	tools: number;
}

/** Count tool calls during a turn; record the turn once it ends. */
function recordTurnEvent(
	turns: Map<string, TurnStats>,
	chatId: string,
	event: AgentEvent
): void {
	const stats = turns.get(chatId);
	if (!stats) {
		return;
	}
	if (event.type === "tool" && event.status !== "started") {
		stats.tools += 1;
		stats.toolErrors += event.status === "error" ? 1 : 0;
		return;
	}
	if (event.type === "error") {
		track("agent_chat.error", { provider: stats.provider });
		return;
	}
	if (event.type === "turn_done" || event.type === "exit") {
		turns.delete(chatId);
		track("agent_chat.turn_completed", {
			provider: stats.provider,
			ended_by: event.type,
			duration_ms: Date.now() - stats.startedAt,
			tool_calls: stats.tools,
			tool_errors: stats.toolErrors,
		});
	}
}

export function ChatPanel({
	boardId,
	onClose,
}: {
	boardId: string;
	onClose: () => void;
}) {
	const bridge = getDesktopBridge();
	if (!bridge) {
		return <WebNotice onClose={onClose} />;
	}
	return <DesktopChat boardId={boardId} bridge={bridge} onClose={onClose} />;
}

function DesktopChat({
	boardId,
	bridge,
	onClose,
}: {
	boardId: string;
	bridge: DesktopBridge;
	onClose: () => void;
}) {
	const [chats, setChats] = useState<Chat[]>(() => loadChats(boardId));
	const [activeId, setActiveId] = useState<string | null>(
		() => chats.toSorted((a, b) => b.updatedAt - a.updatedAt)[0]?.id ?? null
	);
	const [availability, setAvailability] = useState<AgentAvailability[]>([]);
	const [provider, setProvider] = useState<AgentProvider>("codex");
	const [draft, setDraft] = useState("");
	const [running, setRunning] = useState<Set<string>>(() => new Set());
	const scrollRef = useRef<HTMLDivElement>(null);
	/** Per running chat: when its turn started and its tool calls so far. */
	const turnStats = useRef(new Map<string, TurnStats>());

	const active = chats.find((c) => c.id === activeId) ?? null;
	const isRunning = active ? running.has(active.id) : false;

	useEffect(() => {
		saveChats(boardId, chats);
	}, [boardId, chats]);

	useEffect(() => {
		bridge
			.agentAvailability()
			.then((list) => {
				setAvailability(list);
				track("agent_chat.availability_checked", {
					installed: list.filter((a) => a.installed).map((a) => a.provider),
				});
				// Fall back to an installed agent if the default isn't installed.
				setProvider((current) => {
					if (list.find((a) => a.provider === current)?.installed) {
						return current;
					}
					return list.find((a) => a.installed)?.provider ?? current;
				});
			})
			.catch(() => setAvailability([]));
	}, [bridge]);

	useEffect(
		() =>
			bridge.onAgentEvent(({ chatId, event }) => {
				recordTurnEvent(turnStats.current, chatId, event);
				if (event.type === "turn_done" || event.type === "exit") {
					setRunning((s) => {
						const next = new Set(s);
						next.delete(chatId);
						return next;
					});
				}
				setChats((list) =>
					list.map((chat) => {
						if (chat.id !== chatId) {
							return chat;
						}
						const updated = applyAgentEvent(chat, event);
						return event.type === "turn_done"
							? settleRunningTools(updated)
							: updated;
					})
				);
			}),
		[bridge]
	);

	useEffect(() => {
		const el = scrollRef.current;
		if (el) {
			el.scrollTop = el.scrollHeight;
		}
	});

	const updateChat = useCallback((id: string, change: (c: Chat) => Chat) => {
		setChats((list) => list.map((c) => (c.id === id ? change(c) : c)));
	}, []);

	const startChat = () => {
		const chat = createChat(provider);
		setChats((list) => [chat, ...list]);
		setActiveId(chat.id);
		track("agent_chat.new_chat", { provider, chats: chats.length + 1 });
	};

	const send = async (text: string, via: "enter" | "button" | "suggestion") => {
		const message = text.trim();
		if (!message || isRunning) {
			return;
		}
		// An empty chat takes whichever agent is selected now.
		const base =
			active && active.items.length > 0
				? active
				: { ...(active ?? createChat(provider)), provider };
		const chat = addUserMessage(base, message);
		const chatId = chat.id;
		setChats((list) => [chat, ...list.filter((c) => c.id !== chatId)]);
		setActiveId(chatId);
		setDraft("");
		setRunning((s) => new Set(s).add(chatId));
		track("agent_chat.message_sent", {
			provider: chat.provider,
			via,
			first_message: base.items.length === 0,
			messages: chat.items.filter((i) => i.kind === "user").length,
		});
		turnStats.current.set(chatId, {
			provider: chat.provider,
			startedAt: Date.now(),
			tools: 0,
			toolErrors: 0,
		});
		try {
			const token = await ensureAgentToken(bridge);
			await bridge.agentSend({
				apiUrl: env.VITE_SERVER_URL,
				boardId,
				chatId,
				message,
				provider: chat.provider,
				providerSessionId: chat.providerSessionId,
				token,
			});
		} catch (err) {
			turnStats.current.delete(chatId);
			track("agent_chat.start_failed", { provider: chat.provider });
			updateChat(chatId, (c) =>
				applyAgentEvent(c, {
					type: "error",
					message:
						err instanceof Error ? err.message : "Couldn't start the agent",
				})
			);
			setRunning((s) => {
				const next = new Set(s);
				next.delete(chatId);
				return next;
			});
		}
	};

	const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			send(draft, "enter").catch(() => {
				/* handled inside */
			});
		}
	};

	const sortedChats = useMemo(
		() => chats.toSorted((a, b) => b.updatedAt - a.updatedAt),
		[chats]
	);
	const chatProvider =
		active && active.items.length > 0 ? active.provider : provider;

	return (
		<aside className="flex w-96 shrink-0 flex-col border-l bg-card">
			<div className="flex h-12 items-center gap-2 border-b px-3">
				<Select
					disabled={Boolean(active && active.items.length > 0)}
					onValueChange={(v) => {
						setProvider(v as AgentProvider);
						track("agent_chat.provider_changed", { provider: v });
					}}
					value={chatProvider}
				>
					<SelectTrigger aria-label="Agent" className="h-8 w-28 text-xs">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{(["codex", "claude"] as const).map((p) => {
							const status = availability.find((a) => a.provider === p);
							return (
								<SelectItem
									disabled={status ? !status.installed : false}
									key={p}
									value={p}
								>
									{PROVIDER_LABELS[p]}
									{status && !status.installed ? " (not installed)" : ""}
								</SelectItem>
							);
						})}
					</SelectContent>
				</Select>
				{sortedChats.length > 0 && (
					<Select
						onValueChange={(id) => {
							setActiveId(id);
							track("agent_chat.history_opened", { chats: chats.length });
						}}
						value={activeId ?? undefined}
					>
						<SelectTrigger
							aria-label="Chat history"
							className="h-8 min-w-0 flex-1 text-xs"
						>
							<SelectValue placeholder="Chats" />
						</SelectTrigger>
						<SelectContent>
							{sortedChats.map((c) => (
								<SelectItem key={c.id} value={c.id}>
									{c.title} · {PROVIDER_LABELS[c.provider]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				<Button
					aria-label="New chat"
					className="ml-auto"
					onClick={startChat}
					size="icon-sm"
					title="New chat"
					variant="ghost"
				>
					<Plus />
				</Button>
				<Button
					aria-label="Close chat"
					onClick={onClose}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>

			<div
				className="flex flex-1 flex-col gap-2 overflow-y-auto p-3"
				ref={scrollRef}
			>
				{(active?.items.length ?? 0) === 0 ? (
					<div className="flex flex-col gap-2 pt-6 text-center">
						<p className="font-medium text-sm">
							Chat with {PROVIDER_LABELS[chatProvider]} about this board
						</p>
						<p className="text-muted-foreground text-xs">
							It can read and edit the canvas, find plucks in your library and
							run prompts. It runs on your computer with your own account.
						</p>
						<div className="mt-2 flex flex-col gap-1.5">
							{SUGGESTIONS.map((s) => (
								<button
									className="rounded-md border px-3 py-2 text-left text-xs hover:bg-muted"
									key={s}
									onClick={() => {
										send(s, "suggestion").catch(() => {
											/* handled inside */
										});
									}}
									type="button"
								>
									{s}
								</button>
							))}
						</div>
					</div>
				) : (
					active?.items.map((item) => <ChatMessage item={item} key={item.id} />)
				)}
				{isRunning && (
					<p className="flex items-center gap-1.5 text-muted-foreground text-xs">
						<Loader2 className="size-3 animate-spin" />
						{PROVIDER_LABELS[chatProvider]} is working…
					</p>
				)}
			</div>

			<div className="flex flex-col gap-2 border-t p-3">
				<Textarea
					className="max-h-40 min-h-16 resize-none text-sm"
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={onKeyDown}
					placeholder="Ask about this board, or tell it what to change…"
					value={draft}
				/>
				<div className="flex items-center justify-between">
					<p className="text-[11px] text-muted-foreground">
						Enter to send · Shift+Enter for a new line
					</p>
					{isRunning && active ? (
						<Button
							onClick={() => {
								track("agent_chat.stopped", { provider: active.provider });
								bridge.agentStop(active.id).catch(() => {
									/* already stopped */
								});
							}}
							size="sm"
							variant="outline"
						>
							<Square />
							Stop
						</Button>
					) : (
						<Button
							disabled={draft.trim().length === 0}
							onClick={() => {
								send(draft, "button").catch(() => {
									/* handled inside */
								});
							}}
							size="sm"
						>
							<Send />
							Send
						</Button>
					)}
				</div>
			</div>
		</aside>
	);
}
